import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, useCallback, useContext, useEffect, useState, useSyncExternalStore } from "react";
import { useDebouncedCallback } from "use-debounce";
import styles from "./Header.module.scss";
import { GeneralBanner } from "../GeneralBanner/GeneralBanner";
import { GeneralBannerQueryResult } from "../../../../studio/sanity.types";
import { BannerContext } from "../../../main/layout/layout";

// Notify at most every 250ms while scrolling, matching the debounced scroll handler below
const subscribeToScroll = (onChange: () => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const handleScroll = () => {
    if (timeoutId !== undefined) return;
    timeoutId = setTimeout(() => {
      timeoutId = undefined;
      onChange();
    }, 250);
  };
  window.addEventListener("scroll", handleScroll);
  return () => {
    clearTimeout(timeoutId);
    window.removeEventListener("scroll", handleScroll);
  };
};
const getIsScrolled = () => window.scrollY > 0;
const getServerIsScrolled = () => false;
export const MainHeader: React.FC<{
  children: ReactNode | ReactNode[];
  hideOnScroll: boolean;
  generalBannerConfig?: GeneralBannerQueryResult;
  alwaysShrink?: boolean;
}> = t0 => {
  const $ = _c(33);
  const {
    children,
    hideOnScroll,
    generalBannerConfig,
    alwaysShrink
  } = t0;
  const [bannerContext] = useContext(BannerContext);
  const isScrolled = useSyncExternalStore(subscribeToScroll, getIsScrolled, getServerIsScrolled);
  const navbarShrinked = alwaysShrink || isScrolled;
  const [navBarVisible, setNavBarVisible] = useState(true);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  let t1;
  if ($[0] !== lastScrollPosition) {
    t1 = () => {
      if (typeof window !== "undefined") {
        if (window.scrollY > lastScrollPosition && window.scrollY > 160) {
          setNavBarVisible(false);
        } else {
          if (window.scrollY < lastScrollPosition - 300 || window.scrollY <= 160) {
            setNavBarVisible(true);
          }
        }
      }
    };
    $[0] = lastScrollPosition;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const navBarVisibleCheck = t1;
  let t2;
  if ($[2] !== hideOnScroll || $[3] !== navBarVisibleCheck) {
    t2 = () => {
      if (hideOnScroll) {
        navBarVisibleCheck();
      }
      setLastScrollPosition(window.scrollY);
    };
    $[2] = hideOnScroll;
    $[3] = navBarVisibleCheck;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      maxWait: 250
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const debounced = useDebouncedCallback(t2, 250, t3);
  let t4;
  if ($[6] !== debounced) {
    t4 = () => {
      if (typeof window !== "undefined") {
        window.addEventListener("scroll", debounced);
      }
    };
    $[6] = debounced;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t4);
  let containerClasses;
  if ($[8] !== navbarShrinked) {
    containerClasses = [styles.container];
    if (navbarShrinked) {
      containerClasses.push(styles.navbarShrinked);
    }
    $[8] = navbarShrinked;
    $[9] = containerClasses;
  } else {
    containerClasses = $[9];
  }
  let headerStackClasses;
  if ($[10] !== navBarVisible) {
    headerStackClasses = [styles.headerStack];
    if (!navBarVisible) {
      headerStackClasses.push(styles.headerHidden);
    }
    $[10] = navBarVisible;
    $[11] = headerStackClasses;
  } else {
    headerStackClasses = $[11];
  }
  const showGeneralBanner = Boolean(generalBannerConfig) && !bannerContext.generalBannerDismissed;
  let spacerClasses;
  let wrapperClasses;
  if ($[12] !== showGeneralBanner) {
    wrapperClasses = [styles.wrapper];
    if (showGeneralBanner) {
      wrapperClasses.push(styles.hasGeneralBanner);
    }
    spacerClasses = [styles.spacer];
    if (showGeneralBanner) {
      spacerClasses.push(styles.hasGeneralBanner);
    }
    $[12] = showGeneralBanner;
    $[13] = spacerClasses;
    $[14] = wrapperClasses;
  } else {
    spacerClasses = $[13];
    wrapperClasses = $[14];
  }
  const t5 = wrapperClasses.join(" ");
  const t6 = spacerClasses.join(" ");
  let t7;
  if ($[15] !== t6) {
    t7 = <div aria-hidden="true" className={t6} />;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = containerClasses.join(" ");
  const t9 = headerStackClasses.join(" ");
  let t10;
  if ($[17] !== generalBannerConfig || $[18] !== showGeneralBanner) {
    t10 = showGeneralBanner && generalBannerConfig && <GeneralBanner configuration={generalBannerConfig} />;
    $[17] = generalBannerConfig;
    $[18] = showGeneralBanner;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== children) {
    t11 = <div data-cy="header-navbar" className={styles.navbarWrapper}>{children}</div>;
    $[20] = children;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = <div className={t9}>{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t8) {
    t13 = <div data-cy="header" className={t8}>{t12}</div>;
    $[26] = t12;
    $[27] = t8;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t5 || $[31] !== t7) {
    t14 = <div className={t5}>{t7}{t13}</div>;
    $[29] = t13;
    $[30] = t5;
    $[31] = t7;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
};